import { useState, useEffect, useCallback, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router';
import {
    Box, Typography, Button, Stack, Chip, IconButton, Tabs, TabList, Tab, TabPanel,
    Modal, ModalDialog, ModalClose, Input, Textarea, Select, Option, Switch,
} from '@mui/joy';
import { motion, AnimatePresence } from 'framer-motion';

import ArrowBackRoundedIcon  from '@mui/icons-material/ArrowBackRounded';
import AddRoundedIcon        from '@mui/icons-material/AddRounded';
import EditRoundedIcon       from '@mui/icons-material/EditRounded';
import DeleteRoundedIcon     from '@mui/icons-material/DeleteRounded';
import LocalOfferRoundedIcon from '@mui/icons-material/LocalOfferRounded';
import PercentRoundedIcon    from '@mui/icons-material/PercentRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';

import Header from '../../components/Header';
import MemeLoader from '../../components/MemeLoader';
import { AppContext } from '../../context';
import {
    APIGetProducts, APICreateProduct, APIUpdateProduct, APIDeleteProduct,
    APIGetDiscounts, APICreateDiscount, APIUpdateDiscount, APIDeleteDiscount,
    parseApiError,
} from '../../api';


const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    fontSize: 13, color: 'white', flex: 1,
    '&:focus-within': { borderColor: 'var(--joy-palette-primary-500)' },
};


// ────────────────────────────────────────────────────────────────────
//  Модалка товара
// ────────────────────────────────────────────────────────────────────
function ProductModal({ open, item, allDiscounts, onClose, onSave, loading, error }) {
    const isEdit = !!item;
    const [form, setForm] = useState({ name: '', price: '', comment: '', is_active: true });
    // Связанные скидки (новые черновики + существующие из item.discounts_attached)
    const [linkedDiscounts, setLinkedDiscounts] = useState([]);

    useEffect(() => {
        if (open) {
            setForm(item ? {
                name: item.name || '',
                price: String(item.price || ''),
                comment: item.comment || '',
                is_active: item.is_active ?? true,
            } : { name: '', price: '', comment: '', is_active: true });

            // Подгружаем существующие скидки, привязанные к товару
            const attached = item ? allDiscounts.filter(d => d.product === item.id) : [];
            setLinkedDiscounts(attached.map(d => ({
                id: d.id,
                name: d.name,
                is_percent: d.is_percent,
                value_from: String(d.value_from),
                value_to:   String(d.value_to),
                _existing: true,
            })));
        }
    }, [open, item, allDiscounts]);

    const set = field => e => {
        const val = typeof e === 'string' || typeof e === 'boolean' ? e : e.target.value;
        setForm(p => ({ ...p, [field]: val }));
    };

    const addDiscountDraft = () => {
        setLinkedDiscounts(prev => [...prev, {
            tmp_id: `tmp-${Date.now()}`,
            name: '', is_percent: false,
            value_from: '', value_to: '',
            _existing: false,
        }]);
    };
    const updateDraft = (idx, field, val) => {
        setLinkedDiscounts(prev => prev.map((d, i) => i === idx ? { ...d, [field]: val } : d));
    };
    const removeDraft = (idx) => {
        setLinkedDiscounts(prev => prev.filter((_, i) => i !== idx));
    };

    const handleSave = () => {
        if (!form.name.trim()) return;
        const price = parseFloat(form.price);
        if (isNaN(price) || price < 0) return;

        // Готовим payload: новые скидки + существующие (id)
        const drafts = linkedDiscounts
            .filter(d => d.name.trim() && d.value_from && d.value_to)
            .map(d => ({
                id: d.id || null,
                name: d.name.trim(),
                is_percent: d.is_percent,
                value_from: parseFloat(d.value_from),
                value_to: parseFloat(d.value_to),
            }));

        onSave({
            name: form.name.trim(),
            price,
            comment: form.comment.trim(),
            is_active: form.is_active,
            _linkedDiscounts: drafts,   // обрабатывается в handleSaveProduct
        });
    };

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.55)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 440, width: 'calc(100vw - 32px)', p: 3,
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                    {isEdit ? 'Редактировать товар' : 'Новый товар'}
                </Typography>

                <Stack spacing={1.5}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>Название *</Typography>
                        <Input value={form.name} onChange={set('name')} placeholder="Свадебная фотосессия" sx={inputSx} />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>Рекомендуемая цена *</Typography>
                        <Input type="number" value={form.price} onChange={set('price')} placeholder="5000" sx={inputSx}
                            endDecorator={<Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>₽</Typography>}
                        />
                    </Box>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>Комментарий</Typography>
                        <Textarea value={form.comment} onChange={set('comment')} minRows={2} maxRows={4} sx={inputSx} />
                    </Box>

                    {isEdit && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch checked={form.is_active} onChange={(e) => set('is_active')(e.target.checked)} />
                            <Typography sx={{ fontSize: 12 }}>Активен</Typography>
                        </Box>
                    )}

                    {/* ── Скидки, привязанные к товару ─────────────── */}
                    <Box sx={{ pt: 1, borderTop: '1px solid var(--joy-palette-divider)' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'var(--joy-palette-text-secondary)' }}>
                                🏷 Скидки для этого товара
                            </Typography>
                            <Button size="sm" variant="plain"
                                startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                onClick={addDiscountDraft}
                                sx={{ fontSize: 11, color: 'var(--joy-palette-primary-400)' }}>
                                Добавить
                            </Button>
                        </Box>

                        <AnimatePresence mode="popLayout">
                            {linkedDiscounts.length === 0 && (
                                <motion.div
                                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                                >
                                    <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', fontStyle: 'italic' }}>
                                        Скидок для этого товара пока нет
                                    </Typography>
                                </motion.div>
                            )}
                            {linkedDiscounts.map((d, idx) => (
                                <motion.div
                                    key={d.id || d.tmp_id}
                                    layout
                                    initial={{ opacity: 0, x: -20, scale: 0.95 }}
                                    animate={{ opacity: 1, x: 0,   scale: 1    }}
                                    exit={{    opacity: 0, x: 20,  scale: 0.95 }}
                                    transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                                    style={{ marginBottom: 8 }}
                                >
                                    <Box sx={{
                                        p: 1,
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-md)',
                                    }}>
                                        <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5 }}>
                                            <Input size="sm" placeholder="Название"
                                                value={d.name}
                                                onChange={(e) => updateDraft(idx, 'name', e.target.value)}
                                                sx={{ ...inputSx, flex: 1, minWidth: 0 }}
                                            />
                                            <IconButton size="sm" color="danger" variant="plain"
                                                onClick={() => removeDraft(idx)}>
                                                <DeleteRoundedIcon sx={{ fontSize: 14 }} />
                                            </IconButton>
                                        </Box>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                            <Switch size="sm" checked={d.is_percent}
                                                onChange={(e) => updateDraft(idx, 'is_percent', e.target.checked)} />
                                            <Typography sx={{ fontSize: 10 }}>
                                                {d.is_percent ? '%' : '₽'}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, minWidth: 0 }}>
                                            <Input size="sm" type="number" placeholder="От"
                                                value={d.value_from}
                                                onChange={(e) => updateDraft(idx, 'value_from', e.target.value)}
                                                sx={{ ...inputSx, flex: 1, minWidth: 0 }}
                                            />
                                            <Input size="sm" type="number" placeholder="До"
                                                value={d.value_to}
                                                onChange={(e) => updateDraft(idx, 'value_to', e.target.value)}
                                                sx={{ ...inputSx, flex: 1, minWidth: 0 }}
                                            />
                                        </Box>
                                    </Box>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </Box>

                    {error && (
                        <Typography sx={{ fontSize: 12, color: '#f87171' }}>{error}</Typography>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                    <Button fullWidth onClick={handleSave} loading={loading} sx={{
                        background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                        color: '#fff', fontWeight: 700,
                    }}>
                        Сохранить
                    </Button>
                    <Button variant="outlined" onClick={onClose} sx={{ borderColor: 'var(--joy-palette-divider)' }}>
                        Отмена
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


// ────────────────────────────────────────────────────────────────────
//  Модалка скидки
// ────────────────────────────────────────────────────────────────────
function DiscountModal({ open, item, products, onClose, onSave, loading, error }) {
    const isEdit = !!item;
    const [form, setForm] = useState({
        name: '', is_percent: false,
        value_from: '', value_to: '',
        product_id: null, is_active: true,
    });

    useEffect(() => {
        if (open) {
            setForm(item ? {
                name: item.name || '',
                is_percent: item.is_percent || false,
                value_from: String(item.value_from || ''),
                value_to:   String(item.value_to   || ''),
                product_id: item.product || null,
                is_active:  item.is_active ?? true,
            } : { name: '', is_percent: false, value_from: '', value_to: '', product_id: null, is_active: true });
        }
    }, [open, item]);

    const set = field => e => {
        const val = typeof e === 'string' || typeof e === 'boolean' ? e : e.target.value;
        setForm(p => ({ ...p, [field]: val }));
    };

    const handleSave = () => {
        const vf = parseFloat(form.value_from);
        const vt = parseFloat(form.value_to);
        if (!form.name.trim() || isNaN(vf) || isNaN(vt) || vf > vt) return;
        onSave({
            name: form.name.trim(),
            is_percent: form.is_percent,
            value_from: vf,
            value_to: vt,
            product_id: form.product_id,
            is_active: form.is_active,
        });
    };

    return (
        <Modal open={open} onClose={onClose}
            slotProps={{ backdrop: { sx: { backdropFilter: 'blur(8px)', bgcolor: 'rgba(0,0,0,0.55)' } } }}
        >
            <ModalDialog sx={{
                bgcolor: 'var(--joy-palette-background-level1)',
                border: '1px solid var(--joy-palette-divider)',
                borderRadius: 'var(--joy-radius-xl)',
                maxWidth: 440, width: '100%', p: 3,
            }}>
                <ModalClose />
                <Typography sx={{ fontSize: 18, fontWeight: 800, mb: 2 }}>
                    {isEdit ? 'Редактировать скидку' : 'Новая скидка'}
                </Typography>

                <Stack spacing={1.5}>
                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>Название *</Typography>
                        <Input value={form.name} onChange={set('name')} placeholder="Скидка на ДР" sx={inputSx} />
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Switch checked={form.is_percent}
                                onChange={(e) => set('is_percent')(e.target.checked)} />
                        <Typography sx={{ fontSize: 12 }}>
                            {form.is_percent ? 'В процентах (%)' : 'В рублях (₽)'}
                        </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', gap: 1, minWidth: 0 }}>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>От *</Typography>
                            <Input type="number" value={form.value_from} onChange={set('value_from')}
                                sx={{ ...inputSx, width: '100%' }}
                                endDecorator={<Typography sx={{ fontSize: 11 }}>{form.is_percent ? '%' : '₽'}</Typography>}
                            />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>До *</Typography>
                            <Input type="number" value={form.value_to} onChange={set('value_to')}
                                sx={{ ...inputSx, width: '100%' }}
                                endDecorator={<Typography sx={{ fontSize: 11 }}>{form.is_percent ? '%' : '₽'}</Typography>}
                            />
                        </Box>
                    </Box>

                    <Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'var(--joy-palette-text-tertiary)', mb: 0.5 }}>Привязка к товару</Typography>
                        <Select
                            value={form.product_id ?? 'universal'}
                            onChange={(_, v) => set('product_id')(v === 'universal' ? null : v)}
                            placeholder="Универсальная"
                            sx={{
                                color: 'var(--joy-palette-text-primary)',
                                bgcolor: 'var(--joy-palette-background-level2)',
                                border: '1px solid var(--joy-palette-divider)',
                                fontSize: 13,
                                '&:hover': { borderColor: 'rgba(162,38,226,0.4)', bgcolor: 'var(--joy-palette-background-level2)' },
                            }}
                            slotProps={{
                                listbox: {
                                    sx: {
                                        bgcolor: 'var(--joy-palette-background-level2)',
                                        border: '1px solid var(--joy-palette-divider)',
                                        boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                        '& .MuiOption-root': {
                                            color: 'var(--joy-palette-text-primary)',
                                            fontSize: 13,
                                            '&:hover': {
                                                bgcolor: 'rgba(162,38,226,0.12)',
                                                color: 'var(--joy-palette-text-primary)',
                                            },
                                            '&[aria-selected="true"]': {
                                                bgcolor: 'rgba(162,38,226,0.2)',
                                                color: 'var(--joy-palette-primary-300)',
                                                fontWeight: 600,
                                            },
                                        },
                                    },
                                },
                            }}
                        >
                            <Option value="universal">— Универсальная (без товара) —</Option>
                            {products.filter(p => p.is_active).map(p => (
                                <Option key={p.id} value={p.id}>{p.name}</Option>
                            ))}
                        </Select>
                        <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                            {form.product_id
                                ? 'Будет предложена в чеке при выборе этого товара'
                                : 'Можно добавить в любой чек вручную'}
                        </Typography>
                    </Box>

                    {isEdit && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Switch checked={form.is_active} onChange={(e) => set('is_active')(e.target.checked)} />
                            <Typography sx={{ fontSize: 12 }}>Активна</Typography>
                        </Box>
                    )}
                    {error && (
                        <Typography sx={{ fontSize: 12, color: '#f87171' }}>{error}</Typography>
                    )}
                </Stack>

                <Stack direction="row" spacing={1} sx={{ mt: 2.5 }}>
                    <Button fullWidth onClick={handleSave} loading={loading} sx={{
                        background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                        color: '#fff', fontWeight: 700,
                    }}>
                        Сохранить
                    </Button>
                    <Button variant="outlined" onClick={onClose} sx={{ borderColor: 'var(--joy-palette-divider)' }}>
                        Отмена
                    </Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}


// ════════════════════════════════════════════════════════════════════
//  Главный компонент страницы
// ════════════════════════════════════════════════════════════════════
export default function ProductsDiscountsPage() {
    const { token, showToast, hasPerm, onRefreshTick } = useContext(AppContext);
    const navigate = useNavigate();

    const canManageProducts  = hasPerm('finance.products.manage');
    const canManageDiscounts = hasPerm('finance.discounts.manage');

    const [tab, setTab] = useState(0);
    const [products,  setProducts]  = useState([]);
    const [discounts, setDiscounts] = useState([]);
    const [loading,   setLoading]   = useState(true);

    const [productModal,  setProductModal]  = useState({ open: false, item: null, loading: false, error: '' });
    const [discountModal, setDiscountModal] = useState({ open: false, item: null, loading: false, error: '' });
    const [busyId, setBusyId] = useState(null);

    const loadAll = useCallback(async () => {
        setLoading(true);
        const [prods, discs] = await Promise.all([
            APIGetProducts(token),
            APIGetDiscounts(token),
        ]);
        if (Array.isArray(prods)) setProducts(prods);
        if (Array.isArray(discs)) setDiscounts(discs);
        setLoading(false);
    }, [token]);

    useEffect(() => { loadAll(); }, [loadAll]);
    useEffect(() => onRefreshTick(loadAll), [onRefreshTick, loadAll]);

    // ── Товары ───────────────────────────────────────────────────
    const handleSaveProduct = async (form) => {
        setProductModal(p => ({ ...p, loading: true, error: '' }));
        const { _linkedDiscounts, ...productData } = form;

        const productRes = productModal.item
            ? await APIUpdateProduct(token, productModal.item.id, productData)
            : await APICreateProduct(token, productData);

        if (productRes?.error) {
            setProductModal(p => ({ ...p, loading: false, error: parseApiError(productRes.error) }));
            return;
        }

        const productId = productRes.id || productModal.item?.id;

        // Сохраняем привязанные скидки
        if (_linkedDiscounts && _linkedDiscounts.length > 0) {
            for (const d of _linkedDiscounts) {
                const payload = {
                    name: d.name,
                    is_percent: d.is_percent,
                    value_from: d.value_from,
                    value_to: d.value_to,
                    product_id: productId,
                    is_active: true,
                };
                if (d.id) {
                    await APIUpdateDiscount(token, d.id, payload);
                } else {
                    await APICreateDiscount(token, payload);
                }
            }
        }

        setProductModal({ open: false, item: null, loading: false, error: '' });
        showToast(productModal.item ? 'Товар обновлён ✓' : 'Товар создан ✓', 'success');
        loadAll();
    };

    const handleDeleteProduct = async (item) => {
        if (!window.confirm(`Удалить товар "${item.name}"?`)) return;
        setBusyId(`p-${item.id}`);
        const res = await APIDeleteProduct(token, item.id);
        setBusyId(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка'), 'danger');
        } else {
            showToast('Товар деактивирован');
            loadAll();
        }
    };

    // ── Скидки ───────────────────────────────────────────────────
    const handleSaveDiscount = async (form) => {
        setDiscountModal(p => ({ ...p, loading: true, error: '' }));
        const res = discountModal.item
            ? await APIUpdateDiscount(token, discountModal.item.id, form)
            : await APICreateDiscount(token, form);
        if (res?.error) {
            setDiscountModal(p => ({ ...p, loading: false, error: parseApiError(res.error) }));
            return;
        }
        setDiscountModal({ open: false, item: null, loading: false, error: '' });
        showToast(discountModal.item ? 'Скидка обновлена ✓' : 'Скидка создана ✓', 'success');
        loadAll();
    };

    const handleDeleteDiscount = async (item) => {
        if (!window.confirm(`Удалить скидку "${item.name}"?`)) return;
        setBusyId(`d-${item.id}`);
        const res = await APIDeleteDiscount(token, item.id);
        setBusyId(null);
        if (res?.error) {
            showToast(parseApiError(res.error, 'Ошибка'), 'danger');
        } else {
            showToast('Скидка деактивирована');
            loadAll();
        }
    };

    const productById = useMemo(
        () => Object.fromEntries(products.map(p => [p.id, p])),
        [products],
    );

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: 'var(--joy-palette-background-body)' }}>
            <Header />
            <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Button variant="plain" startDecorator={<ArrowBackRoundedIcon />}
                            onClick={() => navigate(-1)}
                            sx={{ color: 'var(--joy-palette-text-secondary)' }}>
                        Назад
                    </Button>
                    <Typography sx={{ fontSize: 20, fontWeight: 800 }}>
                        Товары и скидки
                    </Typography>
                </Box>

                {/* ── Переключатель вкладок — кнопками ──────────── */}
                <Box sx={{
                    display: 'flex',
                    gap: 1,
                    mb: 2,
                    flexDirection: { xs: 'column', sm: 'row' },
                    width: '100%',
                }}>
                    {[
                        { id: 0, label: 'Товары',  icon: <Inventory2RoundedIcon sx={{ fontSize: 16 }} />, count: products.filter(p => p.is_active).length, color: 'var(--joy-palette-primary-500)' },
                        { id: 1, label: 'Скидки',  icon: <LocalOfferRoundedIcon sx={{ fontSize: 16 }} />, count: discounts.filter(d => d.is_active).length, color: '#f59e0b' },
                    ].map(t => {
                        const active = tab === t.id;
                        return (
                            <Box
                                key={t.id}
                                onClick={() => setTab(t.id)}
                                component={motion.div}
                                whileTap={{ scale: 0.97 }}
                                sx={{
                                    flex: { xs: 'none', sm: '0 0 auto' },
                                    width: { xs: '100%', sm: 'auto' },
                                    px: 2.5, py: 1,
                                    borderRadius: 'var(--joy-radius-xl)',
                                    cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', gap: 1,
                                    justifyContent: { xs: 'center', sm: 'flex-start' },
                                    bgcolor: active ? t.color : 'var(--joy-palette-background-level1)',
                                    border: `1.5px solid ${active ? t.color : 'var(--joy-palette-divider)'}`,
                                    color: active ? '#fff' : 'var(--joy-palette-text-primary)',
                                    boxShadow: active ? `0 4px 16px ${t.color}55` : 'none',
                                    transition: 'all 0.2s ease',
                                    '&:hover': {
                                        borderColor: t.color,
                                        bgcolor: active ? t.color : `${t.color}15`,
                                    },
                                }}
                            >
                                {t.icon}
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'inherit' }}>
                                    {t.label}
                                </Typography>
                                <Box sx={{
                                    px: 0.8, py: 0.1,
                                    borderRadius: '999px',
                                    bgcolor: active ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)',
                                    fontSize: 11, fontWeight: 700,
                                }}>
                                    {t.count}
                                </Box>
                            </Box>
                        );
                    })}
                </Box>

                {/* ── Контент вкладок с анимацией смены ───────────── */}
                <AnimatePresence mode="wait">
                    {tab === 0 && (
                        <motion.div
                            key="products"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* ── ВКЛАДКА: ТОВАРЫ ─────────────────────────── */}
                            <Box>
                                {canManageProducts && (
                                    <Button startDecorator={<AddRoundedIcon />}
                                            onClick={() => setProductModal({ open: true, item: null, loading: false, error: '' })}
                                            sx={{
                                                mb: 2,
                                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                                color: '#fff', fontWeight: 700,
                                            }}>
                                        Добавить товар
                                    </Button>
                                )}

                                {loading ? <MemeLoader /> : products.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 6, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Товаров пока нет
                                    </Box>
                                ) : (
                                    <Stack spacing={1}>
                                        <AnimatePresence mode="popLayout">
                                            {products.map((p, idx) => (
                                                <motion.div
                                                    key={p.id}
                                                    layout
                                                    initial={{ opacity: 0, y: 10 }}
                                                    animate={{ opacity: 1, y: 0, transition: { delay: idx * 0.04 } }}
                                                    exit={{ opacity: 0, x: -100 }}
                                                >
                                                    <Box sx={{
                                                        bgcolor: 'var(--joy-palette-background-level1)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        borderRadius: 'var(--joy-radius-lg)',
                                                        p: 1.5,
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        opacity: p.is_active ? 1 : 0.5,
                                                    }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{p.name}</Typography>
                                                                {!p.is_active && <Chip size="sm" color="neutral">Неактивен</Chip>}
                                                            </Box>
                                                            <Typography sx={{ fontSize: 13, color: 'var(--joy-palette-primary-300)', fontWeight: 700 }}>
                                                                {Number(p.price).toLocaleString('ru-RU')} ₽
                                                            </Typography>
                                                            {p.comment && (
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                                                    {p.comment}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {canManageProducts && (
                                                            <Stack direction="row" spacing={0.5}>
                                                                <IconButton size="sm" onClick={() => setProductModal({ open: true, item: p, loading: false, error: '' })}
                                                                    sx={{ bgcolor: 'rgba(162,38,226,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(162,38,226,0.18)' } }}
                                                                >
                                                                    <EditRoundedIcon sx={{ fontSize: 20, color: 'primary.300' }} />
                                                                </IconButton>
                                                                <IconButton size="sm" color="danger"
                                                                    loading={busyId === `p-${p.id}`}
                                                                    onClick={() => handleDeleteProduct(p)}
                                                                    sx={{ bgcolor: 'rgba(248,113,113,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(248,113,113,0.18)' } }}
                                                                >
                                                                    <DeleteRoundedIcon sx={{ fontSize: 20, color: '#f87171' }} />
                                                                </IconButton>
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                </motion.div>
                                            ))}
                                        </AnimatePresence>
                                    </Stack>
                                )}
                            </Box>
                        </motion.div>
                    )}

                    {tab === 1 && (
                        <motion.div
                            key="discounts"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 20 }}
                            transition={{ duration: 0.2 }}
                        >
                            {/* ── ВКЛАДКА: СКИДКИ ─────────────────────────── */}
                            <Box>
                                {canManageDiscounts && (
                                    <Button startDecorator={<AddRoundedIcon />}
                                            onClick={() => setDiscountModal({ open: true, item: null, loading: false, error: '' })}
                                            sx={{
                                                mb: 2,
                                                background: 'linear-gradient(135deg, var(--joy-palette-primary-500) 0%, var(--joy-palette-primary-300) 100%)',
                                                color: '#fff', fontWeight: 700,
                                            }}>
                                        Добавить скидку
                                    </Button>
                                )}

                                {loading ? <MemeLoader /> : discounts.length === 0 ? (
                                    <Box sx={{ textAlign: 'center', py: 6, color: 'var(--joy-palette-text-tertiary)' }}>
                                        Скидок пока нет
                                    </Box>
                                ) : (
                                    <Stack spacing={1}>
                                        <AnimatePresence mode="popLayout">
                                            {discounts.map(d => {
                                                const product = d.product ? productById[d.product] : null;
                                                return (
                                                    <Box key={d.id} sx={{
                                                        bgcolor: 'var(--joy-palette-background-level1)',
                                                        border: '1px solid var(--joy-palette-divider)',
                                                        borderRadius: 'var(--joy-radius-lg)',
                                                        p: 1.5,
                                                        display: 'flex', alignItems: 'center', gap: 1.5,
                                                        opacity: d.is_active ? 1 : 0.5,
                                                    }}>
                                                        <Box sx={{ flex: 1 }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{d.name}</Typography>
                                                                {!d.is_active && <Chip size="sm" color="neutral">Неактивна</Chip>}
                                                                {product
                                                                    ? <Chip size="sm" color="primary">для «{product.name}»</Chip>
                                                                    : <Chip size="sm" color="success">универсальная</Chip>}
                                                            </Box>
                                                            <Typography sx={{ fontSize: 13, color: '#f59e0b', fontWeight: 700, mt: 0.3 }}>
                                                                {d.value_from === d.value_to
                                                                    ? `${d.value_from}${d.is_percent ? '%' : '₽'}`
                                                                    : `${d.value_from} – ${d.value_to}${d.is_percent ? '%' : '₽'}`}
                                                            </Typography>
                                                        </Box>
                                                        {canManageDiscounts && (
                                                            <Stack direction="row" spacing={0.5}>
                                                                <IconButton size="sm" onClick={() => setDiscountModal({ open: true, item: d, loading: false, error: '' })}
                                                                    sx={{ bgcolor: 'rgba(162,38,226,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(162,38,226,0.18)' } }}
                                                                >
                                                                    <EditRoundedIcon sx={{ fontSize: 20, color: 'primary.300' }} />
                                                                </IconButton>
                                                                <IconButton size="sm" color="danger"
                                                                    loading={busyId === `d-${d.id}`}
                                                                    onClick={() => handleDeleteDiscount(d)}
                                                                    sx={{ bgcolor: 'rgba(248,113,113,0.5)', borderRadius: 'var(--joy-radius-md)', '&:hover': { bgcolor: 'rgba(248,113,113,0.18)' } }}
                                                                >
                                                                    <DeleteRoundedIcon sx={{ fontSize: 20, color: '#f87171' }} />
                                                                </IconButton>
                                                            </Stack>
                                                        )}
                                                    </Box>
                                                );
                                            })}
                                        </AnimatePresence>
                                    </Stack>
                                )}
                            </Box>
                        </motion.div>
                    )}
                </AnimatePresence>
            </Box>

            <ProductModal
                open={productModal.open} item={productModal.item}
                allDiscounts={discounts}
                loading={productModal.loading} error={productModal.error}
                onClose={() => setProductModal({ open: false, item: null, loading: false, error: '' })}
                onSave={handleSaveProduct}
            />

            <DiscountModal
                open={discountModal.open} item={discountModal.item}
                products={products}
                loading={discountModal.loading} error={discountModal.error}
                onClose={() => setDiscountModal({ open: false, item: null, loading: false, error: '' })}
                onSave={handleSaveDiscount}
            />
        </Box>
    );
}